import { motion } from 'framer-motion';
import { type MouseEvent as ReactMouseEvent } from 'react';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Colors } from '../../themes/colors';
import arrow from '../../images/arrow_right.svg';
import type { ICollapsibleCardProps } from './interfaces';

export const CollapsibleCard = ({
  title,
  TitleComponent,
  description,
  children,
  isOpen,
  onToggle,
  backgroundColor,
  headerPadding,
  noShadow,
  titleColor,
  headerStyle,
}: ICollapsibleCardProps) => {
  const collapsableVariants = {
    open: {
      maxHeight: 2000,
      opacity: 1,
      marginTop: description ? -48 : 0,
      paddingBottom: 16,
    },
    closed: {
      maxHeight: 0,
      opacity: 0,
    },
  };

  const arrowVariants = {
    open: {
      rotate: -90,
    },
    closed: {
      rotate: 90,
    },
  };

  const opacityVariants = {
    show: {
      opacity: 1,
    },
    hide: {
      opacity: 0,
    },
  };

  const handleToggle = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onToggle();
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: backgroundColor ? Colors[backgroundColor] : Colors.white,
        borderRadius: 8,
        boxShadow: noShadow ? 'none' : '0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
      }}
    >
      <button
        type="button"
        style={{
          height: '100%',
          all: 'unset',
          display: 'flex',
          justifyContent: 'space-between',
          cursor: 'pointer',
          padding: headerPadding || '32px 40px',
          ...headerStyle,
        }}
        onClick={handleToggle}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {TitleComponent ? (
              <TitleComponent />
            ) : (
              <span
                style={{
                  fontFamily: 'Nunito',
                  color: titleColor ? Colors[titleColor] : Colors.primary_500,
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {title}
              </span>
            )}
            {description && (
              <motion.span
                animate={isOpen ? 'hide' : 'show'}
                transition={{ duration: 0.4 }}
                variants={opacityVariants}
                style={{ marginTop: isOpen ? 4 : 0 }}
              >
                <Typography italic as="span" color={colors.neutral[500]}>
                  {description}
                </Typography>
              </motion.span>
            )}
          </div>
        </div>
        <div>
          <motion.img
            initial="closed"
            animate={isOpen ? 'open' : 'closed'}
            transition={{ duration: 0.4 }}
            variants={arrowVariants}
            alt="arrow"
            src={arrow}
          />
        </div>
      </button>
      <motion.div
        initial="closed"
        animate={isOpen ? 'open' : 'closed'}
        transition={{ duration: 0.5 }}
        style={{
          overflow: isOpen ? 'visible' : 'hidden',
          height: 'auto',
          backgroundColor: backgroundColor ? Colors[backgroundColor] : Colors.white,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
        variants={collapsableVariants}
      >
        {children}
      </motion.div>
    </div>
  );
};
